import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { DatePicker, Input, Button, Tag, Badge, Modal, Tooltip, Card } from "antd";
import {
    SearchOutlined,
    ReloadOutlined,
    DownloadOutlined,
    EyeOutlined,
    FilterOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { getRequest } from "../../../Utils/AppApi";
import moment from "moment";

const { RangePicker } = DatePicker;

// API Constants
const LIST_TRANSACTION_AUDIT_LOGS = "/admin/transactionAudit/list";
const EXPORT_TRANSACTION_AUDIT_LOGS = "/admin/transactionAudit/export/csv";

const TransactionAuditLogsPage = ({ history }) => {
    const csvLink = useRef();
    const [isLoading, setLoading] = useState(false);
    const [getData, setData] = useState([]);
    const [getCsvData, setCsvData] = useState([]);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(1);
    const [isLimit, setLimit] = useState(50);

    // Filter states
    const [searchUserId, setSearchUserId] = useState("");
    const [searchTransactionId, setSearchTransactionId] = useState("");
    const [searchPurpose, setSearchPurpose] = useState("");
    const [searchBase, setSearchBase] = useState("");
    // Date range is mandatory – default to today
    const [dateRange, setDateRange] = useState([moment().startOf('day'), moment().endOf('day')]);

    // Modal state
    const [selectedLog, setSelectedLog] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    // Format date for API
    const formatDateForAPI = (date) => {
        if (!date) return null;
        try {
            return moment(date).format('YYYY-MM-DD');
        } catch (error) {
            return null;
        }
    };

    // Build query params – always include dates (mandatory)
    const buildParams = (forExport = false) => {
        const params = forExport ? {} : { page, limit: isLimit };

        if (searchUserId.trim()) params.userId = searchUserId.trim();
        if (searchTransactionId.trim()) params.transactionId = searchTransactionId.trim();
        if (searchPurpose.trim()) params.purpose = searchPurpose.trim();
        if (searchBase.trim()) params.base = searchBase.trim();

        // Always include startDate and endDate (they are now mandatory)
        if (dateRange && dateRange[0]) {
            params.startDate = formatDateForAPI(dateRange[0]);
        } else {
            // Fallback to today if somehow missing
            params.startDate = moment().format('YYYY-MM-DD');
        }
        if (dateRange && dateRange[1]) {
            params.endDate = formatDateForAPI(dateRange[1]);
        } else {
            params.endDate = moment().format('YYYY-MM-DD');
        }

        return params;
    };

    // Object to query string
    const objectToQueryString = (obj) => {
        const str = [];
        for (const p in obj) {
            if (obj.hasOwnProperty(p) && obj[p] !== undefined && obj[p] !== null && obj[p] !== "") {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        }
        return str.join("&");
    };

    // Fetch transaction logs
    const getDataReports = (newLimit) => {
        setLoading(true);

        const queryParams = buildParams();
        const queryString = objectToQueryString(queryParams);

        getRequest(`${LIST_TRANSACTION_AUDIT_LOGS}?${queryString}`, history)
            .then((response) => {
                if (response.success === true) {
                    setData(response.data?.transactions || []);
                    setTotalRecord(response.data?.pagination?.total || 0);
                    setPage(response.data?.pagination?.page || 1);
                } else {
                    setData([]);
                    setTotalRecord(0);
                    setPage(1);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
            });
    };

    // Manual CSV Export (FIXED)
    const handleManualExport = () => {
        setLoading(true);
        const queryParams = buildParams(true);
        const queryString = objectToQueryString(queryParams);

        getRequest(`${EXPORT_TRANSACTION_AUDIT_LOGS}?${queryString}`, history)
            .then((response) => {
                if (response.success === true) {
                    console.log("Export API Response:", response); // Debug log
                    
                    // Handle different response formats
                    let dataArray = [];
                    
                    if (Array.isArray(response.data)) {
                        dataArray = response.data;
                    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                        dataArray = response.data.data;
                    } else if (response.data && Array.isArray(response.data.transactions)) {
                        dataArray = response.data.transactions;
                    }
                    
                    console.log("Data array for export:", dataArray);

                    if (dataArray.length === 0) {
                        toast.info("No data to export");
                        setLoading(false);
                        return;
                    }

                    // Prepare CSV content
                    const headers = [
                        'Transaction ID',
                        'Base',
                        'User ID',
                        'Purpose',
                        'Created Date',
                        'Created Time',
                        'Timestamp'
                    ];
                    
                    const csvRows = [];
                    csvRows.push(headers.join(','));
                    
                    dataArray.forEach(item => {
                        const transactionId = item['Transaction ID'] || item.transactionId || '';
                        const base = item['Base'] || item.base || '';
                        const userId = item['User ID'] || item.userId || '';
                        const purpose = item['Purpose'] || item.purpose || '';
                        
                        let createdDate = item['Created Date'] || '';
                        let createdTime = item['Created Time'] || '';
                        let timestamp = item['Timestamp'] || item.createdAt || '';
                        
                        if (item.createdAt && !createdDate) {
                            const date = moment(item.createdAt);
                            createdDate = date.format("DD/MM/YYYY");
                            createdTime = date.format("HH:mm:ss");
                            timestamp = date.toISOString();
                        }
                        
                        const row = [
                            `"${transactionId.toString().replace(/"/g, '""')}"`,
                            `"${base.toString().replace(/"/g, '""')}"`,
                            `"${userId.toString().replace(/"/g, '""')}"`,
                            `"${purpose.toString().replace(/"/g, '""')}"`,
                            `"${createdDate.toString().replace(/"/g, '""')}"`,
                            `"${createdTime.toString().replace(/"/g, '""')}"`,
                            `"${timestamp.toString().replace(/"/g, '""')}"`
                        ];
                        csvRows.push(row.join(','));
                    });
                    
                    const csvContent = csvRows.join('\n');
                    
                    const blob = new Blob([csvContent], { 
                        type: 'text/csv;charset=utf-8;' 
                    });
                    const link = document.createElement('a');
                    const url = URL.createObjectURL(blob);
                    link.setAttribute('href', url);
                    link.setAttribute('download', `transaction-audit-${moment().format('DD-MM-YYYY-HH-mm-ss')}.csv`);
                    link.style.visibility = 'hidden';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    
                    toast.success(`Exported ${dataArray.length} records successfully`);
                } else {
                    toast.error(response?.message || "Export failed");
                }
                setLoading(false);
            })
            .catch((error) => {
                console.log("Export error:", error);
                toast.error("Export failed");
                setLoading(false);
            });
    };

    // Handle search – validate date range
    const handleSearch = () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            toast.error("Please select a valid date range.");
            return;
        }
        setPage(1);
        getDataReports();
    };

    // Handle clear filters – reset date range to today (mandatory)
    const handleClear = () => {
        setSearchUserId("");
        setSearchTransactionId("");
        setSearchPurpose("");
        setSearchBase("");
        setDateRange([moment().startOf('day'), moment().endOf('day')]);
        setPage(1);
        // Optionally auto‑search after clear? Usually user clicks Search again.
    };

    // Handle key press for search
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Handle page change
    const handlePageChange = (page) => {
        setPage(page);
    };

    // Handle rows per page change
    const handleRowsPerPageChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    // Handle view details
    const handleViewDetails = (row) => {
        setSelectedLog(row);
        setIsModalVisible(true);
    };

    // Table columns (unchanged)
    const columns = [
        {
            name: 'Transaction ID',
            selector: row => row.transactionId,
            cell: row => (
                <Tooltip title="Click to view details">
                    <span
                        className="text-blue-600 cursor-pointer hover:underline"
                        onClick={() => handleViewDetails(row)}
                        style={{
                            fontFamily: "'Courier New', monospace",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block"
                        }}
                    >
                        {row.transactionId}
                    </span>
                </Tooltip>
            ),
            width: "200px",
            minWidth: "180px",
            maxWidth: "200px",
            sortable: true,
        },
        {
            name: 'User ID',
            selector: row => row.userId,
            cell: row => (
                <Tag color="blue" style={{
                    fontSize: "12px",
                    padding: "2px 8px",
                    whiteSpace: "nowrap"
                }}>
                    {row.userId}
                </Tag>
            ),
            width: "130px",
            minWidth: "120px",
            maxWidth: "140px",
            sortable: true,
        },
        {
            name: 'Base',
            selector: row => row.base,
            cell: row => (
                <Badge
                    color={row.base === 'SI' ? "green" : "blue"}
                    text={row.base}
                    style={{
                        fontSize: "12px",
                        whiteSpace: "nowrap"
                    }}
                />
            ),
            width: "90px",
            minWidth: "80px",
            maxWidth: "100px",
            center: true,
            sortable: true,
        },
        {
            name: 'Purpose',
            selector: row => row.purpose,
            cell: row => (
                <Tooltip title={row.purpose}>
                    <div style={{
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "300px"
                    }}>
                        {row.purpose}
                    </div>
                </Tooltip>
            ),
            width: "350px",
            minWidth: "250px",
            maxWidth: "400px",
            sortable: true,
        },
        {
            name: 'Date & Time',
            selector: row => row.createdAt,
            cell: row => (
                <div style={{
                    fontSize: "12px",
                    whiteSpace: "nowrap"
                }}>
                    <div>{moment(row.createdAt).format("DD/MM/YY")} {moment(row.createdAt).format("HH:mm:ss")}</div>
                </div>
            ),
            width: "160px",
            minWidth: "100px",
            maxWidth: "130px",
            sortable: true,
        },
        {
            name: '',
            selector: row => row._id,
            cell: row => (
                <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => handleViewDetails(row)}
                    style={{
                        color: "#1890ff",
                        minWidth: "40px"
                    }}
                />
            ),
            width: "80px",
            minWidth: "70px",
            maxWidth: "90px",
            center: true,
        },
    ];

    // Initial data fetch – on mount and page change
    useEffect(() => {
        getDataReports();
    }, [page]);

    return (
        <div style={{
            background: "#f8f9fa",
            minHeight: "100vh",
            padding: "20px"
        }}>
            <div style={{
                maxWidth: "1400px",
                margin: "0 auto"
            }}>

                {/* Header */}
                <div style={{
                    marginBottom: "24px"
                }}>
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px"
                    }}>
                        <div>
                            <h1 style={{
                                margin: 0,
                                fontSize: "24px",
                                fontWeight: "600",
                                color: "#1a202c"
                            }}>
                                Transaction Ids Logs
                            </h1>
                        </div>

                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleManualExport}
                                type="primary"
                                loading={isLoading}
                            >
                                Export
                            </Button>
                            {/* Keep as fallback */}
                            <CSVLink
                                data={getCsvData}
                                headers={[
                                    { label: "Transaction ID", key: "Transaction ID" },
                                    { label: "Base", key: "Base" },
                                    { label: "User ID", key: "User ID" },
                                    { label: "Purpose", key: "Purpose" },
                                    { label: "Created Date", key: "Created Date" },
                                    { label: "Created Time", key: "Created Time" },
                                    { label: "Timestamp", key: "Timestamp" },
                                ]}
                                filename={`transaction-audit-${moment().format('DD-MM-YYYY')}.csv`}
                                className="hidden"
                                ref={csvLink}
                                target="_blank"
                                uFEFF={false}
                                asyncOnClick={false}
                            />
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <Card style={{
                    marginBottom: "24px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0"
                }}>
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px"
                    }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                        }}>
                            <FilterOutlined style={{ color: "#4a5568" }} />
                            <span style={{
                                fontWeight: "500",
                                color: "#4a5568"
                            }}>
                                Filters
                            </span>
                        </div>

                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleClear}
                            >
                                Clear
                            </Button>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                            >
                                Search
                            </Button>
                        </div>
                    </div>

                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: "16px"
                    }}>
                        <div>
                            <label style={{
                                display: "block",
                                marginBottom: "6px",
                                fontSize: "13px",
                                color: "#4a5568"
                            }}>
                                Date Range <span style={{ color: "red" }}>*</span>
                            </label>
                            <RangePicker
                                style={{ width: "100%" }}
                                format="DD/MM/YYYY"
                                value={dateRange}
                                onChange={(dates) => setDateRange(dates || [moment().startOf('day'), moment().endOf('day')])}
                            />
                        </div>

                        <div>
                            <label style={{
                                display: "block",
                                marginBottom: "6px",
                                fontSize: "13px",
                                color: "#4a5568"
                            }}>
                                User ID
                            </label>
                            <Input
                                placeholder="Enter User ID"
                                value={searchUserId}
                                onChange={(e) => setSearchUserId(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                        </div>

                        <div>
                            <label style={{
                                display: "block",
                                marginBottom: "6px",
                                fontSize: "13px",
                                color: "#4a5568"
                            }}>
                                Transaction ID
                            </label>
                            <Input
                                placeholder="Enter Transaction ID"
                                value={searchTransactionId}
                                onChange={(e) => setSearchTransactionId(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                        </div>

                        <div>
                            <label style={{
                                display: "block",
                                marginBottom: "6px",
                                fontSize: "13px",
                                color: "#4a5568"
                            }}>
                                Base
                            </label>
                            <Input
                                placeholder="SI, SP, SN, etc"
                                value={searchBase}
                                onChange={(e) => setSearchBase(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                        </div>

                        <div>
                            <label style={{
                                display: "block",
                                marginBottom: "6px",
                                fontSize: "13px",
                                color: "#4a5568"
                            }}>
                                Purpose
                            </label>
                            <Input
                                placeholder="Search purpose"
                                value={searchPurpose}
                                onChange={(e) => setSearchPurpose(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                        </div>
                    </div>
                </Card>

                {/* Data Table */}
                <Card style={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    overflow: "hidden"
                }}>
                    <div style={{
                        padding: "16px",
                        borderBottom: "1px solid #e2e8f0",
                        background: "#f8f9fa"
                    }}>
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}>
                            <div>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: "16px",
                                    fontWeight: "600"
                                }}>
                                    Transaction Records
                                </h3>
                                <p style={{
                                    margin: "4px 0 0 0",
                                    fontSize: "13px",
                                    color: "#718096"
                                }}>
                                    {getData.length} of {getTotalRecord} records
                                </p>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <span style={{ fontSize: "13px", color: "#718096" }}>Show:</span>
                                <select
                                    style={{
                                        padding: "6px 12px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "4px",
                                        fontSize: "13px"
                                    }}
                                    value={isLimit}
                                    onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                                >
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={200}>200</option>
                                    <option value={500}>500</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div style={{ padding: 0 }}>
                        <DataTable
                            columns={columns}
                            data={getData}
                            progressPending={isLoading}
                            pagination
                            paginationServer
                            paginationTotalRows={getTotalRecord}
                            paginationRowsPerPageOptions={[50, 100, 200, 500]}
                            paginationPerPage={isLimit}
                            onChangeRowsPerPage={handleRowsPerPageChange}
                            onChangePage={handlePageChange}
                            highlightOnHover
                            fixedHeader
                            fixedHeaderScrollHeight="calc(100vh - 400px)"
                            dense
                            noDataComponent={
                                <div style={{
                                    padding: "48px 0",
                                    textAlign: "center",
                                    color: "#718096"
                                }}>
                                    <div style={{ fontSize: "16px", marginBottom: "8px" }}>
                                        No transaction logs found
                                    </div>
                                    <p style={{ fontSize: "14px" }}>
                                        Try adjusting your search filters
                                    </p>
                                </div>
                            }
                            customStyles={{
                                table: {
                                    style: {
                                        tableLayout: 'fixed',
                                    },
                                },
                                headCells: {
                                    style: {
                                        backgroundColor: '#f8f9fa',
                                        padding: '12px 16px',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: '#4a5568',
                                        borderBottom: '1px solid #e2e8f0',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    },
                                },
                                cells: {
                                    style: {
                                        padding: '12px 16px',
                                        fontSize: '12px',
                                        color: '#4a5568',
                                        borderBottom: '1px solid #f1f5f9',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    },
                                },
                                rows: {
                                    style: {
                                        '&:hover': {
                                            backgroundColor: '#f8fafc',
                                        },
                                    },
                                },
                            }}
                        />
                    </div>
                </Card>

                {/* Modal for Transaction Details */}
                <Modal
                    title="Transaction Details"
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    footer={[
                        <Button key="close" onClick={() => setIsModalVisible(false)}>
                            Close
                        </Button>
                    ]}
                    width={600}
                >
                    {selectedLog && (
                        <div style={{ padding: "8px 0" }}>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "16px",
                                marginBottom: "24px"
                            }}>
                                <div>
                                    <div style={{ fontSize: "13px", color: "#718096", marginBottom: "4px" }}>
                                        Transaction ID
                                    </div>
                                    <div style={{
                                        fontFamily: "'Courier New', monospace",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        wordBreak: "break-all"
                                    }}>
                                        {selectedLog.transactionId}
                                    </div>
                                </div>

                                <div>
                                    <div style={{ fontSize: "13px", color: "#718096", marginBottom: "4px" }}>
                                        User ID
                                    </div>
                                    <Tag color="blue" style={{ wordBreak: "break-all" }}>{selectedLog.userId}</Tag>
                                </div>

                                <div>
                                    <div style={{ fontSize: "13px", color: "#718096", marginBottom: "4px" }}>
                                        Base
                                    </div>
                                    <Badge
                                        color={selectedLog.base === 'SI' ? "green" : "blue"}
                                        text={selectedLog.base}
                                    />
                                </div>

                                <div>
                                    <div style={{ fontSize: "13px", color: "#718096", marginBottom: "4px" }}>
                                        Date & Time
                                    </div>
                                    <div>
                                        {moment(selectedLog.createdAt).format("DD/MM/YYYY HH:mm:ss")}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div style={{ fontSize: "13px", color: "#718096", marginBottom: "8px" }}>
                                    Purpose
                                </div>
                                <div style={{
                                    padding: "12px",
                                    background: "#f8f9fa",
                                    borderRadius: "4px",
                                    border: "1px solid #e2e8f0",
                                    fontSize: "14px",
                                    lineHeight: "1.5",
                                    wordBreak: "break-word"
                                }}>
                                    {selectedLog.purpose}
                                </div>
                            </div>
                        </div>
                    )}
                </Modal>
            </div>
        </div>
    );
};

export default TransactionAuditLogsPage;